import { computed, observable } from "mobx";
import { RouterModel } from "mst-react-router";
import smoothscroll from "smoothscroll-polyfill";
import { CONTINENTS } from "../shared/mocks/continents";
import { Continent } from "./EntityStores/ContinentStore";
import { State } from "./EntityStores/StateStore";
import { GoogleAnalyticsStore } from "./UtilityStore/GoogleAnalyticsStore";
import { Screen } from "./UtilityStore/ScreenStore";
import { IUser } from '../components/Auth';

smoothscroll.polyfill();

/**
 * MAINSTORE
 *
 * @author nicolacastellani@vas.it
 * @description this is the main store, here you can handle the substores and relative actions
 */
export class MainStore {
  @observable _continents?= CONTINENTS;
  @computed get continents(): any[] {
    return this._continents ? this._continents : [];
  }

  @observable user: IUser;

  /// SELECTED PROPS

  @observable selectedState?: any;
  @observable selectedContinent?: any;

  /// UTILS

  @observable screen: Screen = new Screen(document.body.offsetWidth <= 900);
  @observable router: RouterModel = RouterModel.create();
  googleAnalytics: GoogleAnalyticsStore = new GoogleAnalyticsStore();

  /// ANIMATION RELATED PROPS
  @observable onPageTransition: boolean = false;
  @observable loading = true;
  @observable videoOpening = false;

  @computed get nextState(): State | null {
    return this.getNextState();
  }

  @computed get previousState(): State | null {
    return this.getPreviousState();
  }

  constructor(user: IUser) {
    /*if (!this.user.token) {
      /// get a fresh new token
      this.loadUser();
    }*/
    //this.user = { name: '', matricola: '', id: '' };
    setTimeout(async () => {
      this.checkSelectedContinent();
      this.checkSelectedState();
    }, 2000);

    setTimeout(async () => {
      this.setLoading(false);
    }, 4000);

    this.user = user;
  }

  initHandlers() {
    /// handle window resize
    window.onresize = (e: Event) => {
      this.screen.isMobile = document.body.offsetWidth <= 900;
    };
  }

  /**
   * Returns next state, if exists
   */
  getNextState(
    continents = this._continents,
    isPrevious = false
  ): State | null {
    if (!continents) return null;
    if (isPrevious === true) {
      continents = continents.filter(c => !c.disabled).reverse();
    } else {
      continents = continents.filter(c => !c.disabled);
    }
    let currentContinent: Continent;
    const shifter = isPrevious ? -1 : 1;
    if (this.selectedState) {
      let result: any = null;
      let isNextContinent: boolean = false;
      continents.forEach((c: Continent) => {
        if (c.disabled) return;
        if (c.states) {
          /// prevent useless loop
          if (result) return;
          var states = c.states.filter(s => !s.disabled);
          states.forEach((state: State, index: number) => {
            if (isNextContinent && currentContinent !== c) {
              /// the first state of this continent is the next one in the application
              result = states[isPrevious ? states.length - 1 : 0];
              isNextContinent = false;
              currentContinent = c;
              return;
            }
            if (state.name === this.selectedState.name) {
              /// state at next position is the next, but we need to check if exists
              if (states[index + shifter]) {
                /// ok return that
                result = states[index + shifter];
                return;
              } else {
                /// the next continent first state is the next one
                isNextContinent = true;
                currentContinent = c;
                return;
              }
            }
          });
        }
      });
      if (!result) {
        /// return to first state
        const firstContinent = continents[0];
        const { states } = firstContinent;
        if (firstContinent && states) {
          return states[isPrevious ? states.length - 1 : 0];
        }
      } else {
        return result;
      }
    }
    return null;
  }

  /**
   * Returns next state, if exists
   */
  getPreviousState(): State | null {
    /// the trick is simple
    /// reverse the continent object and handle like next state
    if (!this._continents) return null;
    return this.getNextState(this._continents, true);
  }

  async checkSelectedState() {
    const { pathname } = this.router.location;
    if (pathname.indexOf("/country/") > -1) {
      const splittedUrl = pathname.split("/");
      const countryName = splittedUrl[splittedUrl.length - 1];
      this.continents.forEach(c => {
        c.states.forEach((s: State) => {
          if (this.selectedState) return;
          if (s.name.toLowerCase() === countryName) {
            this.selectedState = s;
            s.selected = true;
            this.googleAnalytics.onDetailsNavigation(s, this.user);
            return;
          }
        });
      });
    } else {
      await this.googleAnalytics.onHomeNavigation(this.user);
    }
  }

  setLoading(loading: boolean) {
    this.loading = loading;
  }

  checkSelectedContinent() {
    const { pathname } = this.router.location;
    if (pathname.indexOf("/continent/") > -1) {
      const splittedUrl = pathname.split("/");
      const continentName = splittedUrl[splittedUrl.length - 1];
      this.continents.forEach(c => {
        if (c.name.toLowerCase() === continentName) {
          this.selectedContinent = c;
          c.selected = true;
        }
      });
    }
  }


  selectState(state?: State, preventRouting = false) {
    if (!this._continents) return null;
    const continent = this._continents.filter(
      (c: Continent) => c.states.filter(s => s.selected).length
    )[0];

    /// if exists a continent with a selected state
    if (continent) {
      (continent as any).states.map((s: State) => (s.selected = false));
    }
    if (
      state &&
      (!this.selectedState || state.name !== this.selectedState.name)
    ) {
      state.selected = true;
      this.selectedState = state;
      if (!preventRouting) {
        debugger;
        this.googleAnalytics.onCountryNavigation(state, this.user);
        this.router.push("/country/" + state.name.toLowerCase());
      }
    } else {
      this.selectedState = null;
      if (!preventRouting) this.router.push("/");
    }
  }

  selectContinent(continent: Continent) {
    if (!this._continents) return null;
    this.selectedState = null;
    this._continents.forEach((c: Continent) => {
      if (c.name === continent.name) {
        c.selected = true;
        this.selectedContinent = c;
      } else {
        c.selected = false;
      }
      c.states.forEach(s => (s.selected = false));
    });

    if (this.selectedContinent) {
      this.router.push(
        "/continent/" + this.selectedContinent.name.toLowerCase()
      );
    }
  }

  getSelectedStateByName(name: string) {
    if (!this._continents) return null;
    let result = null;
    this._continents.forEach((c: Continent) => {
      c.states.forEach((s: State) => {
        if (s.name === name) {
          result = s;
        }
      });
    });
    return result;
  }

  openStateDetail(state: State) {
    this.onPageTransition = true;
    const layouter = document.getElementById("cspi_layouter");
    setTimeout(() => {
      if (state !== this.selectedState) this.selectState(state, true);

      if (this.screen.isMobile) {
        window.scrollTo({ top: 0 });
      } else if (layouter) {
        layouter.scrollIntoView(true);
      }

      this.router.replace("/details/country/" + state.name.toLowerCase());
      this.googleAnalytics.onDetailsNavigation(state, this.user);
    }, 1000);
    setTimeout(() => {
      this.onPageTransition = false;
    }, 2500);
  }

  backHome() {
    this.onPageTransition = true;
    setTimeout(async () => {
      this.selectState(this.selectedState);
      this.router.replace("/");
      await this.googleAnalytics.onHomeNavigation(this.user);
      setTimeout(() => {
        this.onPageTransition = false;
      }, 1500);
    }, 1000);
  }

  closeContinent() {
    this.continents.forEach(c => {
      c.selected = false;
    });

    this.selectedContinent = null;
    this.router.push("/");
  }

  getMarkers() {
    if (!this._continents) return;
    const result: any[] = [];
    let count = 0;
    this._continents
      .filter(continent => !continent.disabled)
      .forEach((c: Continent) => {
        c.states
          .filter(s => !s.disabled)
          .forEach((s, sI) =>
            result.push({
              id: count++,
              city: s.name,
              color: "black",
              coordinates: [s.coordinates![0], s.coordinates![1]],
              value: 0
            })
          );
      });
    return result;
  }

  openStateOrContinent(name: string) {
    if (!this._continents) return;
    let founded = false;
    this._continents.forEach((c: Continent) => {
      if (c.name === name) {
        this.selectContinent(c);
        founded = true;
        return;
      }
      c.states.forEach((s: State) => {
        if (s.name === name) {
          this.selectState(s);
          founded = true;
          return;
        }
      });
    });
    if (!founded) {
      this.closeContinent();
      this.selectState(this.selectedState);
    }
  }

  openVideo() {
    this.videoOpening = true;
  }

  closeVideo() {
    this.videoOpening = false;
  }
}

/* const store: MainStore = new MainStore(window.USER); */
export default MainStore;
